import { type LucideIcon, SearchX, SlidersHorizontal } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface EmptyStateProps {
  icon: LucideIcon
  title: string
  description?: string
  hint?: string
  action?: {
    label: string
    onClick: () => void
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  className?: string
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  hint,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-16 px-6 text-center",
        className
      )}
    >
      {/* Icon with layered rings */}
      <div className="relative mb-6">
        <div className="absolute inset-0 rounded-full bg-primary/5 scale-[2.2] blur-xl" />
        <div className="relative flex items-center justify-center rounded-2xl bg-muted/60 border border-border/60 p-5 shadow-sm">
          <Icon className="h-9 w-9 text-muted-foreground/70" strokeWidth={1.5} />
        </div>
      </div>

      <h3 className="text-base font-semibold tracking-tight mb-1.5">{title}</h3>

      {description && (
        <p className="text-sm text-muted-foreground max-w-[280px] leading-relaxed mb-5">
          {description}
        </p>
      )}

      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
          {action && (
            <Button onClick={action.onClick} size="sm" className="min-w-[120px]">
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}

      {hint && (
        <p className="text-xs text-muted-foreground/60 max-w-[260px] leading-relaxed border-t border-border/40 pt-4 mt-1">
          {hint}
        </p>
      )}
    </div>
  )
}

interface FilteredEmptyStateProps {
  entityName: string
  searchValue?: string
  onClear?: () => void
  className?: string
}

export function FilteredEmptyState({
  entityName,
  searchValue,
  onClear,
  className,
}: FilteredEmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center py-12 px-4 text-center", className)}>
      <div className="relative mb-4">
        <div className="absolute inset-0 rounded-full bg-muted/60 scale-[2] blur-lg" />
        <div className="relative flex items-center justify-center rounded-xl bg-muted/50 border border-border/50 p-3.5">
          {searchValue ? (
            <SearchX className="h-6 w-6 text-muted-foreground/60" strokeWidth={1.5} />
          ) : (
            <SlidersHorizontal className="h-6 w-6 text-muted-foreground/60" strokeWidth={1.5} />
          )}
        </div>
      </div>
      <p className="text-sm font-medium mb-0.5">
        No {entityName} found
      </p>
      {searchValue ? (
        <p className="text-xs text-muted-foreground mb-3">
          Nothing matches <span className="font-medium text-foreground">&ldquo;{searchValue}&rdquo;</span>
        </p>
      ) : (
        <p className="text-xs text-muted-foreground mb-3">
          Try adjusting your filters
        </p>
      )}
      {onClear && (
        <Button variant="outline" size="sm" onClick={onClear} className="h-7 text-xs px-3">
          Clear filters
        </Button>
      )}
    </div>
  )
}
